import { createFileRoute } from "@tanstack/react-router";
import { Heart, Mountain, Users } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import { SCHEMA_ORG_ID, SITE_URL } from "@/lib/entitySchema";

const CANONICAL = `${SITE_URL}/community`;

const MEMBERSHIPS = [
  { name: "Better Business Bureau of Southern Colorado", role: "Accredited Member · A+ rating", url: "https://www.bbb.org/us/co/colorado-springs" },
  { name: "Colorado Springs Chamber & EDC", role: "Active member · Verified directory listing", url: "https://business.coloradospringschamberedc.com/list/Details/member-directory-info-4791078" },
];

const COMMITMENTS = [
  { icon: Mountain, title: "Colorado-Only Client Roster | Colorado Web Impressions", body: "We turn down out-of-state work to stay rooted in Colorado. Every dollar a client invests with us stays in the Front Range economy." },
  { icon: Users, title: "Free guidance for local non-profits", body: "Chris regularly donates strategy time to Colorado Springs-area non-profits, churches, and small community organizations needing help with their websites or Google Business Profiles." },
  { icon: Heart, title: "Faith-based, family-owned", body: "Family-owned and faith-based since 2006. We don't hide what we believe and we treat clients the way we'd want to be treated." },
];

const PAGE_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "@id": `${CANONICAL}#page`,
      url: CANONICAL,
      name: "Community & Memberships — Colorado Web Impressions",
      description: "Our memberships, community involvement, and commitments to the Colorado small-business community.",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      publisher: { "@id": SCHEMA_ORG_ID },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Community", item: CANONICAL },
      ],
    },
  ],
};

export const Route = createFileRoute("/community")({
  head: () => ({
    meta: [
      { title: "Community & Memberships — Colorado Web Impressions" },
      { name: "description", content: "We work exclusively with Colorado businesses. Browse our Colorado-only client community across Colorado Springs, Denver, Boulder, and the Front Range." },
      { property: "og:title", content: "Colorado-Only Client Roster | Colorado Web Impressions" },
      { property: "og:description", content: "We work exclusively with Colorado businesses. Browse our Colorado-only client community across Colorado Springs, Denver, Boulder, and the Front Range." },
      { property: "og:url", content: CANONICAL },
    
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Community & Memberships — Colorado Web Impressions" },
      { name: "twitter:description", content: "BBB-accredited, Chamber member, Colorado-only roster. How we stay rooted in the Front Range community we serve." },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(PAGE_LD) }],
  }),
  component: CommunityPage,
});

function CommunityPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Rooted in Colorado"
        title={<>Local accountability, <span className="text-brand-gold">in writing.</span></>}
        description="We're not a national agency that opened a Colorado office. We're Colorado-only, BBB-accredited, Chamber-active, and we live where our clients live."
      />

      <section className="bg-background py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground">Memberships & accreditations</h2>
          <ul className="mt-5 grid gap-4 sm:grid-cols-2">
            {MEMBERSHIPS.map((m) => (
              <li key={m.name} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <a href={m.url} target="_blank" rel="noopener noreferrer" className="block transition-smooth hover:text-brand-gold-deep">
                  <div className="text-base font-extrabold text-foreground">{m.name}</div>
                  <div className="mt-1 text-xs font-semibold text-brand-gold-deep">{m.role}</div>
                </a>
              </li>
            ))}
          </ul>

          <h2 className="mt-14 text-2xl font-extrabold text-foreground">How we stay rooted</h2>
          <div className="mt-5 grid gap-5 lg:grid-cols-3">
            {COMMITMENTS.map((c) => (
              <div key={c.title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-brand-gold/10 text-brand-gold-deep">
                  <c.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-extrabold text-foreground">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
